import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Loader2, ArrowLeft, KeyRound } from "lucide-react";

/**
 * Public reset-password page. Landed on via link in the forgot-password
 * email — token is in the ?token= query param. Backend `/auth/reset-password`
 * validates + burns the token, sets the new password, invalidates other
 * sessions via token_version bump, and returns a fresh JWT so we can log the
 * user in on this device without a second round-trip.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const missing = !token;

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — you're logged in");
      await refresh?.();
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed — try requesting a new link");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFAF6] flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to login
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8" data-testid="reset-password-card">
          <div className="w-11 h-11 rounded-xl bg-[#C75D33] text-white flex items-center justify-center mb-4">
            <KeyRound className="w-5 h-5" />
          </div>
          <h1 className="font-display text-2xl font-extrabold text-slate-900">Set a new password</h1>
          {missing ? (
            <div className="mt-4">
              <p className="text-sm text-slate-600">This reset link is missing its token. Request a fresh one from the login page.</p>
              <Button onClick={() => nav("/forgot-password")} className="mt-4 w-full bg-[#0F172A] hover:bg-black rounded-xl h-11 font-bold">
                Request new link
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <p className="text-sm text-slate-600">Enter your new password. Other devices you're signed in on will be logged out.</p>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password (min 8 characters)"
                autoComplete="new-password"
                autoFocus
                required
                data-testid="reset-new-pw-input"
                className="h-11 rounded-xl"
              />
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                required
                data-testid="reset-confirm-pw-input"
                className="h-11 rounded-xl"
              />
              <Button type="submit" disabled={saving || !password || !confirm} data-testid="reset-submit-btn" className="w-full bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-11 font-bold disabled:opacity-40">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set new password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
